import axios, {AxiosError, AxiosInstance} from 'axios'
import {API_URL} from '../../../../_metronic/helpers'
import {hidesPayments, STORE_BUILD_UNAVAILABLE_TEXT} from '../../install-app/core/androidApp'

// The payer's side (print pages, buyer link, payment result) reads without the panel's session: no cookie,
// none of the panel's interceptors, so an expired session never signs a visitor out here.
// Made on first use, so a page that only imports a helper from here (the goods/services ID search) never builds it.
let client: AxiosInstance | undefined
const publicClient = () => (client = client || axios.create({withCredentials: false, timeout: 45000}))

// ---- Shapes the backend answers (camelCase wire names) -------------------------------------------------

export type ProviderField = {
  key: string
  label: string
  type: 'text' | 'secret' | 'number' | 'multiline'
  required: boolean
  help?: string | null
  maxLength: number
}

export type PaymentProvider = {
  code: string
  name: string
  latinName?: string
  kind: 'bank' | 'facilitator'
  status: 'active' | 'documentationOnRequest' | 'discontinued'
  website?: string
  documentationUrl?: string
  documentationVersion?: string
  notes?: string
  color?: string
  amountUnit: 'rial' | 'toman'
  minimumAmount: number
  maximumAmount?: number | null
  supportsSandbox: boolean
  requiresServerIp: boolean
  fields: ProviderField[]
}

export type CompanyGateway = {
  id: number
  providerCode: string
  providerName: string
  title?: string | null
  displayTitle: string
  isEnabled: boolean
  isSandbox: boolean
  sortOrder: number
  settings: Record<string, string>
  secrets: Record<string, boolean>
  isLegacy: boolean
  lastCheckedAt?: string | null
  lastCheckSucceeded?: boolean | null
  lastCheckMessage?: string | null
  createdAt?: string | null
}

export type CompanyPaymentSettings = {
  companyId: number
  isStored: boolean
  isEnabled: boolean
  allowOnInvoices: boolean
  allowOnProformas: boolean
  allowCustomAmount: boolean
  minimumAmount: number
  shareValidityDays: number
  shareAllowDecision: boolean
  shareAllowComment: boolean
  shareAllowReceipt: boolean
  /**
   * «انتخاب پرداخت‌کننده» or «فقط خودکار» (backend 1.0.0.675+); either way a gateway that refuses passes the payment to
   * the next one in the business's order.
   */
  gatewayChoice?: GatewayChoice
  modifiedAt?: string | null
}

export type GatewayChoice = 'payer' | 'automatic'

export type OnlinePaymentSettingsData = {
  companyId: number
  available: boolean
  canChange: boolean
  settings: CompanyPaymentSettings
  gateways: CompanyGateway[]
  callbackBase: string
  serverIp?: string | null
  panelOrigin: string
  /** The commission of a payment through each gateway, by gateway id (its provider's own fee; backend 1.0.0.675+). */
  gatewayFees?: Record<string, string> | null
}

export type PaymentStatusKey = 'pending' | 'paid' | 'failed' | 'cancelled' | 'unknown' | 'reversed' | 'expired'
export type ApplyKey = 'none' | 'pending' | 'applying' | 'applied' | 'failed' | 'notApplicable'

export type PaymentTransaction = {
  id: number
  publicId: string
  orderId: number
  purpose: 'invoice' | 'wallet' | 'stuffid'
  status: PaymentStatusKey
  statusTitle: string
  amount: number
  gatewayTitle?: string | null
  providerCode: string
  isSandbox: boolean
  isProforma: boolean
  invoiceNo?: string | null
  invoiceHash?: string | null
  walletCompanyId?: number | null
  walletCredit?: number | null
  referenceNumber?: string | null
  traceNumber?: string | null
  maskedCard?: string | null
  payerMobile?: string | null
  message?: string | null
  createdAt?: string | null
  verifiedAt?: string | null
  apply: ApplyKey
  applyError?: string | null
  canRetry: boolean
  issue?: {status: ApplyKey; invoiceHash?: string | null; error?: string | null} | null
  fromShare: boolean
  /** The payment whose gateway refused just before, when this one took over from it (backend 1.0.0.675+). */
  fallbackOf?: number | null
}

export type GatewayOption = {
  id: number
  title: string
  providerCode: string
  providerName: string
  color?: string | null
  isSandbox: boolean
  minimumAmount: number
  maximumAmount?: number | null
  amountInTomans: boolean
}

export type CheckoutOptions = {
  available: boolean
  enabled: boolean
  reasonCode?: string | null
  reason?: string | null
  isProforma: boolean
  invoiceNo?: string | null
  invoiceHash?: string | null
  total: number
  paid: number
  remaining: number
  minimum: number
  maximum: number
  allowCustomAmount: boolean
  /** «فقط خودکار»: one «پرداخت» button and the business's order; absent from an older backend (the payer picks). */
  gatewayChoice?: GatewayChoice
  gateways: GatewayOption[]
}

export type GatewayRedirect = {url: string; method: 'GET' | 'POST'; fields: Record<string, string>}

export type StartedPayment = {
  publicId: string
  amount: number
  redirect: GatewayRedirect
  /** The gateway the payer goes to, and the ones that refused before it (backend 1.0.0.675+). */
  gatewayTitle?: string | null
  skipped?: string[] | null
  /** «… در دسترس نبود؛ پرداخت با «…» ادامه می‌یابد.» when another gateway took the payment. */
  notice?: string | null
}

export type PaymentResult = {
  publicId: string
  purpose: 'invoice' | 'wallet' | 'stuffid'
  status: PaymentStatusKey
  statusTitle: string
  amount: number
  walletCredit?: number | null
  gatewayTitle?: string | null
  referenceNumber?: string | null
  traceNumber?: string | null
  maskedCard?: string | null
  paidAt?: string | null
  message?: string | null
  apply: ApplyKey
  applyError?: string | null
  invoiceHash?: string | null
  isProforma: boolean
  issuedInvoiceHash?: string | null
}

// ---- Errors --------------------------------------------------------------------------------------------

export type ApiError = {code?: string; message: string; field?: string}

export const readApiError = (error: unknown, fallback: string): ApiError => {
  const response = (error as AxiosError<{code?: string; message?: string; Message?: string; field?: string}>)?.response
  if (!response) {
    return {code: 'NETWORK', message: 'اتصال به سامانه برقرار نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.'}
  }

  const message = response.data?.message || response.data?.Message
  return {
    code: response.data?.code,
    message: typeof message === 'string' && message.trim() ? message : fallback,
    field: response.data?.field,
  }
}

// ---- Business settings ---------------------------------------------------------------------------------

export const onlinePaymentSettingsKey = (companyId: number) => ['online-payment-settings', companyId]

export const getProviders = async (): Promise<PaymentProvider[]> =>
  (await axios.get<{providers: PaymentProvider[]}>(`${API_URL}/online-payment/providers`)).data.providers

export const getOnlinePaymentSettings = async (companyId: number): Promise<OnlinePaymentSettingsData> =>
  (await axios.get<OnlinePaymentSettingsData>(`${API_URL}/online-payment/settings`, {params: {companyId}})).data

/** confirmCharge: agreed to the one-time fee of «درگاه پرداخت آنلاین» that turning it on takes (409 otherwise). */
export const saveOnlinePaymentSettings = async (
  settings: CompanyPaymentSettings & {confirmCharge?: boolean}
): Promise<OnlinePaymentSettingsData> =>
  (await axios.put<OnlinePaymentSettingsData>(`${API_URL}/online-payment/settings`, settings)).data

export type GatewayInput = {
  companyId: number
  providerCode?: string
  title?: string
  isEnabled: boolean
  isSandbox: boolean
  sortOrder: number
  values: Record<string, string>
  /** Agreed to the one-time fee of «درگاه پرداخت آنلاین» that an enabled gateway takes (409 otherwise). */
  confirmCharge?: boolean
}

export const saveGateway = async (
  input: GatewayInput,
  gatewayId?: number
): Promise<{gateway: CompanyGateway; settings: OnlinePaymentSettingsData}> =>
  gatewayId
    ? (await axios.put(`${API_URL}/online-payment/gateways/${gatewayId}`, input)).data
    : (await axios.post(`${API_URL}/online-payment/gateways`, input)).data

/** «ترتیب درگاه‌ها»: every gateway of the business, first to last (backend 1.0.0.675+). */
export const reorderGateways = async (companyId: number, gatewayIds: number[]): Promise<OnlinePaymentSettingsData> =>
  (await axios.put<OnlinePaymentSettingsData>(`${API_URL}/online-payment/gateways/order`, {companyId, gatewayIds})).data

export const deleteGateway = async (companyId: number, gatewayId: number): Promise<OnlinePaymentSettingsData> =>
  (await axios.delete<OnlinePaymentSettingsData>(`${API_URL}/online-payment/gateways/${gatewayId}`, {params: {companyId}})).data

export const testGateway = async (
  companyId: number,
  gatewayId: number
): Promise<{succeeded: boolean; message: string; gateway: CompanyGateway}> =>
  (await axios.post(`${API_URL}/online-payment/gateways/${gatewayId}/test`, null, {params: {companyId}})).data

export const getTransactions = async (
  companyId: number,
  page: number,
  status?: string
): Promise<{available: boolean; total: number; page: number; pageSize: number; items: PaymentTransaction[]}> =>
  (await axios.get(`${API_URL}/online-payment/transactions`, {params: {companyId, page, pageSize: 15, status}})).data

export const retryApply = async (companyId: number, transactionId: number): Promise<PaymentTransaction> =>
  (await axios.post<PaymentTransaction>(`${API_URL}/online-payment/transactions/${transactionId}/apply`, null, {params: {companyId}})).data

// ---- Payer side ----------------------------------------------------------------------------------------

export const getInvoicePaymentOptions = async (hash: string): Promise<CheckoutOptions> =>
  (await publicClient().get<CheckoutOptions>(`${API_URL}/public/online-payment/invoice/${encodeURIComponent(hash)}`)).data

export type StartPaymentInput = {gatewayId?: number; amount?: number; payerMobile?: string; page?: 'new' | 'classic'}

export const startInvoicePayment = async (hash: string, input: StartPaymentInput): Promise<StartedPayment> =>
  (await publicClient().post<StartedPayment>(`${API_URL}/public/online-payment/invoice/${encodeURIComponent(hash)}/start`, input)).data

export const startSharePayment = async (token: string, input: StartPaymentInput): Promise<StartedPayment> =>
  (await publicClient().post<StartedPayment>(`${API_URL}/public/online-payment/share/${encodeURIComponent(token)}/start`, input)).data

export const getPaymentResult = async (publicId: string): Promise<PaymentResult> =>
  (await publicClient().get<PaymentResult>(`${API_URL}/public/online-payment/result/${encodeURIComponent(publicId)}`)).data

/**
 * Sends the browser to the gateway: a plain navigation for a GET, an auto-submitted form for a POST (the
 * gateway sees the panel page as the referrer, which some of them check against the registered domain).
 */
export const goToGateway = (redirect: GatewayRedirect) => {
  // The Bazaar and Myket builds never reach a bank, even from a button left visible (the app refuses it too).
  if (hidesPayments()) throw new Error(STORE_BUILD_UNAVAILABLE_TEXT)
  if (redirect.method !== 'POST') {
    window.location.assign(redirect.url)
    return
  }

  const form = document.createElement('form')
  form.method = 'POST'
  form.action = redirect.url
  form.style.display = 'none'
  Object.entries(redirect.fields || {}).forEach(([name, value]) => {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  })
  document.body.appendChild(form)
  form.submit()
}

// ---- Amounts -------------------------------------------------------------------------------------------

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹'
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩'

/** Persian and Arabic digits to Latin, separators and spaces dropped. */
export const parseAmount = (text: string): number | undefined => {
  const latin = (text || '')
    .replace(/[۰-۹]/g, (digit) => String(PERSIAN_DIGITS.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)))
    .replace(/[,،٬\s]/g, '')
  if (!/^\d+$/.test(latin)) return undefined
  const value = Number(latin)
  return Number.isSafeInteger(value) ? value : undefined
}

/** Rials with Persian digits and grouping: ۱٬۲۵۰٬۰۰۰. */
export const rial = (value?: number | null) =>
  value === null || value === undefined || !Number.isFinite(value) ? '—' : Math.trunc(value).toLocaleString('fa-IR')

/** What a Toman gateway can take: the amount rounded down to whole tomans. */
export const payableFor = (amount: number, gateway?: GatewayOption) =>
  gateway?.amountInTomans ? Math.floor(amount / 10) * 10 : Math.trunc(amount)
